import { Accordion } from 'components';
import {
  type ResultData,
  ResultTemplateKey,
  SUGGESTION_FIELD,
  type SuggestionItem,
} from 'types/result';
import { useVitalParametersEditorContext } from 'providers/VitalParametersEditorProvider';
import { useResultSectionsEditorContext } from 'providers/ResultSectionsEditorProvider';
import { useDiagnosedIcdsEditorContext } from 'providers/DiagnosedIcdsEditorProvider';
import { useAutosaveStatusContext } from 'providers/AutosaveStatusProvider';
import { getSectionManualNote } from 'utils';
import { DiagnosisSection } from 'widgets/results/DiagnosisSection/DiagnosisSection';
import { DiagnosisLoadingIndicator } from 'widgets/results/DiagnosisSection/DiagnosisLoadingIndicator';
import { ResultSection } from 'widgets/results/ResultSection';
import { SectionTitle } from 'widgets/results/ResultSection.styles';
import { EbmProposalsSection } from 'widgets/results/EbmProposalsSection';
import { VitalParametersSection } from 'widgets/results/VitalParametersSection';
import { useTranslation } from 'react-i18next';
import {
  DiagnosisLoadingSkeletonBarPrimary,
  DiagnosisLoadingSkeletonBarSecondary,
  DiagnosisLoadingSkeletonGroup,
  DiagnosisLoadingWrapper,
} from './ResultSummaryTab.styles';

const NO_SUGGESTIONS: SuggestionItem[] = [];

interface ResultSummaryTabProps {
  result: ResultData;
}

export function ResultSummaryTab({ result }: ResultSummaryTabProps) {
  const { t } = useTranslation();
  const autoStatus = useAutosaveStatusContext();
  const { hasDetectedVitalParameters } = useVitalParametersEditorContext();
  const { sectionKeys } = useResultSectionsEditorContext();
  const { hasDiagnosedIcds, icdPending, diagnosedIcdsTitle } = useDiagnosedIcdsEditorContext();

  return (
    <div id="result-panel-summary" role="tabpanel" aria-labelledby="result-tab-summary">
      <div className="rounded-lg border border-border bg-card px-2 mb-5">
        <Accordion
          type="single"
          collapsible
          data-ph-no-autocapture
          defaultValue={hasDetectedVitalParameters ? 'vitalParameters' : undefined}
        >
          <VitalParametersSection autoStatus={autoStatus} />
        </Accordion>
      </div>
      {sectionKeys.map(({ key, title }) => {
        if (!hasDiagnosedIcds && icdPending && key === ResultTemplateKey.Diagnosis) {
          return (
            <DiagnosisLoadingWrapper key="diagnosed-icds-loading">
              <SectionTitle>{diagnosedIcdsTitle}</SectionTitle>
              <DiagnosisLoadingIndicator>
                <DiagnosisLoadingSkeletonGroup>
                  <DiagnosisLoadingSkeletonBarPrimary />
                  <DiagnosisLoadingSkeletonBarSecondary />
                </DiagnosisLoadingSkeletonGroup>
              </DiagnosisLoadingIndicator>
            </DiagnosisLoadingWrapper>
          );
        }

        if (hasDiagnosedIcds && key === ResultTemplateKey.Diagnosis) {
          return (
            <DiagnosisSection
              key="diagnosed-icds"
              title={diagnosedIcdsTitle}
              autoStatus={autoStatus['diagnosedIcdsSection']}
              selectionAutoStatus={autoStatus['diagnosisSelection']}
              placeholderText={t('result.diagnosisSection.noCodes')}
              pendingMoreDiagnoses={icdPending}
            />
          );
        }

        const field = SUGGESTION_FIELD[key];
        const aiSuggestions = (field && result.suggestions?.[field]) || NO_SUGGESTIONS;

        return (
          <ResultSection
            key={`${result.id}-${key}`}
            sectionKey={key}
            title={title}
            value={getSectionManualNote(key, result)}
            autoStatus={autoStatus}
            aiSuggestions={aiSuggestions}
            showSuggestionHint={Boolean(field)}
          />
        );
      })}
      <EbmProposalsSection
        proposals={result?.ebmProposals}
        durationAutoStatus={autoStatus['ebmDuration']}
        selectionAutoStatus={autoStatus['ebmSelection']}
      />
    </div>
  );
}
